import React, { useEffect, useMemo, useState } from 'react';
import {
  Globe,
  GraduationCap,
  BadgeCheck,
  FileX,
  ArrowRight,
  X,
  ChevronRight,
} from 'lucide-react';
import DashboardCard from './DashboardCard';
import type { ComplianceAlertItem, ReportAttentionDetail } from './types';

const iconMap = {
  passport: Globe,
  graduation: GraduationCap,
  badge: BadgeCheck,
  'file-x': FileX,
};

interface ComplianceAlertsProps {
  items: ComplianceAlertItem[];
  loading?: boolean;
}

interface ActiveComplianceModal {
  title: string;
  description: string;
  details: ReportAttentionDetail[];
}

const ComplianceAlerts: React.FC<ComplianceAlertsProps> = ({ items, loading }) => {
  const [activeModal, setActiveModal] = useState<ActiveComplianceModal | null>(null);

  useEffect(() => {
    if (!activeModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeModal]);

  const allDetails = useMemo(
    () =>
      items.flatMap((alert) =>
        (alert.details ?? []).map((row) => ({
          ...row,
          id: `${alert.id}-${row.id}`,
          secondary: `${alert.label} · ${row.secondary}`,
        })),
      ),
    [items],
  );

  const openInNewTab = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const openAlert = (alert: ComplianceAlertItem) => {
    if (!alert.details || alert.details.length === 0) return;
    setActiveModal({
      title: alert.label,
      description: alert.description,
      details: alert.details,
    });
  };

  const openAllCompliance = () => {
    if (allDetails.length === 0) return;
    setActiveModal({
      title: 'Compliance Alerts',
      description: 'Visas, training, licenses, and absences needing attention',
      details: allDetails,
    });
  };

  if (loading) {
    return (
      <DashboardCard title="Compliance Alerts">
        <div className="px-5 pb-5 h-48 animate-pulse bg-gray-50 rounded-lg" />
      </DashboardCard>
    );
  }

  return (
    <>
      <DashboardCard title="Compliance Alerts">
        <div className="px-5 pb-4">
          <div className="space-y-3">
            {items.map((alert) => {
              const Icon = iconMap[alert.icon];
              const clickable = (alert.details?.length ?? 0) > 0;
              return (
                <button
                  key={alert.id}
                  type="button"
                  onClick={() => openAlert(alert)}
                  disabled={!clickable}
                  className={`w-full flex items-center justify-between py-2 border-b border-gray-50 last:border-0 text-left ${
                    clickable ? 'hover:bg-gray-50/80 rounded-md -mx-1 px-1 cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${alert.iconBg}`}>
                      <Icon className={`h-4 w-4 ${alert.iconColor}`} />
                    </div>
                    <span className="text-sm text-gray-700">{alert.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-2 inline-flex items-center gap-1">
                    {alert.countLabel}
                    {clickable ? <ChevronRight className="h-3.5 w-3.5 text-gray-400" /> : null}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={openAllCompliance}
            disabled={allDetails.length === 0}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            View Compliance Report
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </DashboardCard>

      {activeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="compliance-alert-title"
          onClick={() => setActiveModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="min-w-0">
                <h3 id="compliance-alert-title" className="text-base font-semibold text-gray-900">
                  {activeModal.title}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">{activeModal.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {activeModal.details.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => openInNewTab(row.href)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-blue-50/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{row.primary}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{row.secondary}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                </button>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComplianceAlerts;
